import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { usePentaRq, type components } from '@penta/penta-api';
import { Language } from '@penta/i18n-init';
import { formatLanguageToLocaleRegion } from '../utils/localeUtils';
import { Modal, Dropdown, Button, Input } from '@penta/penta-shared';
import { useTranslation } from 'react-i18next';
import { useLanguageToggle } from './LanguageContext';
import styles from './ClientContext.module.scss';

type DetailedClientInfo = components['schemas']['ClientInfo'];
type User = components['schemas']['User'];

export interface ClientInfo {
  id: number;
  name: string;
}

export interface UserPreferences {
  clientId?: number;
  language?: Language;
}

export interface ClientContextType {
  clients: ClientInfo[];
  user: User | null;
  selectedClientId: number | null;
  selectedClient: ClientInfo | null;
  detailedClientInfo: DetailedClientInfo | null;
  setSelectedClientId: (id: number) => void;
  updateLanguagePreference: (language: Language) => void;
  saveUserPreferences: (clientId: number, language: Language) => void;
  isLoading: boolean;
  error: Error | null;
}

export const ClientContext = createContext<ClientContextType | null>(null);

// Retry up to 10 times for network/5xx errors with exponential backoff capped at 30s
const retryInit = (failureCount: number, error: unknown) => {
  if (failureCount >= 10) return false;
  const err = error as { status?: number };
  if (!err?.status || err.status >= 500) return true;
  return false;
};
const retryDelayInit = (attempt: number) =>
  Math.min(1000 * 2 ** attempt, 30000);

export const ClientContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const api = usePentaRq();
  const [manualClientId, setManualClientId] = useState<number | null>(null);
  const [manualLanguage, setManualLanguage] = useState<Language | null>(null);
  const [readyClientId, setReadyClientId] = useState<number | null>(null);
  const [clientInfoError, setClientInfoError] = useState<Error | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [prefSaveFailed, setPrefSaveFailed] = useState(false);

  const {
    data: clientsData,
    isPending: isClientsPending,
    isError: isClientsError,
    error: clientsError,
  } = api.useQuery(
    'get',
    '/client',
    {},
    { retry: retryInit, retryDelay: retryDelayInit },
  );
  const {
    data: userData,
    isPending: isUserPending,
    isError: isUserError,
    error: userError,
  } = api.useQuery(
    'get',
    '/user',
    {},
    { retry: retryInit, retryDelay: retryDelayInit },
  );

  const { mutate: mutateClientInfo, data: detailedClientInfoData } =
    api.useMutation('post', '/client/info', {
      retry: retryInit,
      retryDelay: retryDelayInit,
    });
  const {
    mutateAsync: mutatePreferences,
    mutate: mutatePreferencesSync,
    isPending: isMutatingPreferences,
  } = api.useMutation('put', '/user/preferences', {
    retry: retryInit,
    retryDelay: retryDelayInit,
  });

  const { t, i18n } = useTranslation();
  const { handleChangeLanguage } = useLanguageToggle();
  const [prefTempLang, setPrefTempLang] = useState<Language>(Language.CS);
  const [prefTempClient, setPrefTempClient] = useState<number | null>(null);

  const clients: ClientInfo[] = useMemo(
    () =>
      (clientsData?.clients ?? []).filter(
        (c): c is ClientInfo => c.id !== undefined && c.name !== undefined,
      ),
    [clientsData?.clients],
  );

  // Default values for preferences form
  useEffect(() => {
    if (clients.length > 0 && prefTempClient === null) {
      setPrefTempClient(clients[0].id);
    }
  }, [clients, prefTempClient]);

  useEffect(() => {
    if (
      i18n.language &&
      Object.values(Language).includes(i18n.language as Language)
    ) {
      setPrefTempLang(i18n.language as Language);
    }
  }, [i18n.language]);

  const backendPreferences = (userData?.preferences as UserPreferences) ?? {};
  const userPreferences: UserPreferences = {
    ...backendPreferences,
    language: manualLanguage ?? backendPreferences.language,
    clientId: manualClientId ?? backendPreferences.clientId,
  };
  const prefClientId = manualClientId ?? backendPreferences.clientId;

  // We are awaiting preferences if data is loaded but preferences are incomplete
  const isAwaitingPreferences =
    !!userData &&
    clients.length > 0 &&
    (!prefClientId || !userPreferences.language);

  // Resolve target client ID prioritizing manual selection, then user preferences
  const targetClientId = manualClientId ?? prefClientId ?? null;

  // Sync application language with user's saved preferences from the backend
  // We intentionally omit i18n.language from deps so it only triggers when backend data arriving actually changes
  useEffect(() => {
    if (!isAwaitingPreferences && userPreferences.language) {
      const normalizedLang = formatLanguageToLocaleRegion(
        userPreferences.language as string,
      );
      if (i18n.language !== normalizedLang) {
        handleChangeLanguage(normalizedLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAwaitingPreferences, userPreferences.language, handleChangeLanguage]);

  const saveUserPreferences = useCallback(
    async (clientId: number, language: Language) => {
      setIsSaving(true);
      try {
        setManualClientId(clientId);
        setManualLanguage(language);

        await mutatePreferences({
          body: {
            clientId: clientId,
            language: language,
          },
        });
        // Intentionally not calling refetchUser() here because if the backend fails to persist,
        // it would reset our optimistic local state.
      } catch (err) {
        console.error('Failed to save preferences', err);
        setPrefSaveFailed(true);
      } finally {
        setIsSaving(false);
      }
    },
    [mutatePreferences],
  );

  useEffect(() => {
    if (
      !isAwaitingPreferences &&
      targetClientId !== null &&
      targetClientId !== readyClientId
    ) {
      setClientInfoError(null);
      mutateClientInfo(
        {
          body: { id: targetClientId },
        },
        {
          onSuccess: () => {
            setReadyClientId(targetClientId);
          },
          onError: (err) => {
            setClientInfoError(
              err instanceof Error ? err : new Error('Failed to set client'),
            );
          },
        },
      );
    }
  }, [isAwaitingPreferences, targetClientId, readyClientId, mutateClientInfo]);

  const setSelectedClientId = useCallback(
    (id: number) => {
      setManualClientId(id);
      if (!isAwaitingPreferences) {
        // Background sync when user changes client normally
        const currentLang = userPreferences.language ?? Language.CS;
        mutatePreferencesSync({
          body: {
            clientId: id,
            language: currentLang as Language,
          },
        });
      }
    },
    [isAwaitingPreferences, userPreferences.language, mutatePreferencesSync],
  );

  const updateLanguagePreference = useCallback(
    (language: Language) => {
      setManualLanguage(language);
      if (!isAwaitingPreferences && targetClientId !== null) {
        mutatePreferencesSync({
          body: {
            clientId: targetClientId,
            language: language,
          },
        });
      }
    },
    [isAwaitingPreferences, targetClientId, mutatePreferencesSync],
  );

  const selectedClientId = readyClientId;
  const selectedClient = clients.find((c) => c.id === selectedClientId) ?? null;

  // We are loading if queries are pending, awaiting preferences, or waiting for client info sync
  const isLoading =
    isClientsPending ||
    isUserPending ||
    isAwaitingPreferences ||
    (targetClientId !== null && targetClientId !== readyClientId);

  const error =
    clientInfoError ??
    (clientsError as Error | null) ??
    (userError as Error | null);

  // Fatal error: any part of the init flow failed after all retries
  const isFatalError =
    isClientsError || isUserError || prefSaveFailed || !!clientInfoError;

  const contextValue = useMemo(
    () => ({
      clients,
      user: (userData as User | null) ?? null,
      selectedClientId,
      selectedClient,
      detailedClientInfo: detailedClientInfoData ?? null,
      setSelectedClientId,
      updateLanguagePreference,
      saveUserPreferences,
      isLoading,
      error,
    }),
    [
      clients,
      userData,
      selectedClientId,
      selectedClient,
      detailedClientInfoData,
      setSelectedClientId,
      updateLanguagePreference,
      saveUserPreferences,
      isLoading,
      error,
    ],
  );

  const languageItems = useMemo(
    () => [
      { id: Language.CS, label: t('preferences.languages.cs') },
      { id: Language.SK, label: t('preferences.languages.sk') },
      { id: Language.EN, label: t('preferences.languages.en') },
    ],
    [t],
  );

  const clientDropdownItems = useMemo(
    () =>
      clients.map((c) => ({
        id: c.id.toString(),
        label: c.name,
      })),
    [clients],
  );

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
      {isFatalError && (
        <div
          className={styles['fatalErrorOverlay']}
          role="alert"
          data-testid="fatal-error-overlay"
        >
          <div className={styles['fatalErrorContent']}>
            <p className={styles['fatalErrorTitle']}>{t('initError.title')}</p>
            <p className={styles['fatalErrorDesc']}>
              {t('initError.description')}
            </p>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
              data-testid="fatal-error-reload-btn"
            >
              {t('initError.reload')}
            </Button>
          </div>
        </div>
      )}
      {!isFatalError && isAwaitingPreferences && (
        <Modal
          open={true}
          data-testid="onboarding-preferences-modal"
          title={t('preferences.title')}
          description={t('preferences.description')}
          closeOnInteractOutside={false}
          closeOnEscape={false}
        >
          <div className={styles['formContainer']}>
            <div className={styles['fieldGroup']}>
              <label className={styles['label']}>
                {t('preferences.languageLabel')}
              </label>
              <Dropdown
                data-testid="onboarding-lang-dropdown"
                value={prefTempLang}
                onValueChange={(val) => {
                  const lang = val as Language;
                  setPrefTempLang(lang);
                  handleChangeLanguage(lang);
                }}
                trigger={
                  <div className={styles['selectTriggerWrapper']}>
                    <Input
                      readOnly
                      tabIndex={-1}
                      data-testid="onboarding-lang-trigger"
                      className={styles['selectInput']}
                      value={
                        prefTempLang === Language.CS
                          ? t('preferences.languages.cs')
                          : prefTempLang === Language.SK
                            ? t('preferences.languages.sk')
                            : prefTempLang === Language.EN
                              ? t('preferences.languages.en')
                              : t('preferences.selectPlaceholder')
                      }
                      placeholder={t('preferences.selectPlaceholder')}
                    />
                  </div>
                }
                items={languageItems}
              />
            </div>

            {clients.length > 1 && (
              <div className={styles['fieldGroup']}>
                <label className={styles['label']}>
                  {t('preferences.clientLabel')}
                </label>
                <Dropdown
                  data-testid="onboarding-client-dropdown"
                  value={prefTempClient ? prefTempClient.toString() : ''}
                  onValueChange={(val) => setPrefTempClient(parseInt(val, 10))}
                  trigger={
                    <div className={styles['selectTriggerWrapper']}>
                      <Input
                        readOnly
                        tabIndex={-1}
                        data-testid="onboarding-client-trigger"
                        className={styles['selectInput']}
                        value={
                          prefTempClient
                            ? clients.find((c) => c.id === prefTempClient)
                                ?.name || ''
                            : t('preferences.selectPlaceholder')
                        }
                        placeholder={t('preferences.selectPlaceholder')}
                      />
                    </div>
                  }
                  items={clientDropdownItems}
                />
              </div>
            )}

            <div className={styles['buttonWrapper']}>
              <Button
                variant="primary"
                className={styles['onboardingSaveButton']}
                fullWidth
                data-testid="onboarding-save-btn"
                loading={isMutatingPreferences || isSaving}
                loadingText={t('preferences.saving')}
                onClick={() => {
                  if (prefTempClient !== null) {
                    saveUserPreferences(prefTempClient, prefTempLang);
                  }
                }}
                disabled={
                  prefTempClient === null || isMutatingPreferences || isSaving
                }
              >
                {t('preferences.save')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientContextProvider');
  }
  return context;
};
